const CART_DRAWER_TIMER_STORAGE_KEY = 'cartDrawerReservationExpiry';
const CART_DRAWER_TIMER_DURATION_MS = 10 * 60 * 1000;
const CART_REWARD_GIFT_PROPERTY = '_free_gift';
const CART_REWARD_GIFT_TIER_PROPERTY = '_free_gift_tier';
const CART_REWARD_GIFT_FREE_LINE_PRICE = 0;

const resetCartDrawerTimer = () => {
    sessionStorage.removeItem(CART_DRAWER_TIMER_STORAGE_KEY);
};

const getOrCreateCartDrawerExpiry = () => {
    const now = Date.now();
    const existingExpiry = Number(
        sessionStorage.getItem(CART_DRAWER_TIMER_STORAGE_KEY)
    );
    if (existingExpiry && existingExpiry > now) {
        return existingExpiry;
    }

    const nextExpiry = now + CART_DRAWER_TIMER_DURATION_MS;
    sessionStorage.setItem(CART_DRAWER_TIMER_STORAGE_KEY, nextExpiry.toString());
    return nextExpiry;
};

const formatTimer = (remainingMs) => {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
};

const initCartDrawerTimer = () => {
    const timerEls = document.querySelectorAll('[data-cart-drawer-timer]');
    if (!timerEls.length) {
        return;
    }

    const expiry = getOrCreateCartDrawerExpiry();
    const updateTimer = () => {
        const remainingMs = expiry - Date.now();
        const formattedTime = formatTimer(remainingMs);
        timerEls.forEach((timerEl) => {
            timerEl.textContent = formattedTime;
        });

        if (remainingMs <= 0 && window.cartDrawerTimerIntervalId) {
            clearInterval(window.cartDrawerTimerIntervalId);
            window.cartDrawerTimerIntervalId = null;
        }
    };

    updateTimer();

    if (window.cartDrawerTimerIntervalId) {
        clearInterval(window.cartDrawerTimerIntervalId);
    }

    window.cartDrawerTimerIntervalId = setInterval(updateTimer, 1000);
};

class CartRewardProgress extends HTMLElement {
    constructor() {
        super();
        this.fillEl = this.querySelector('[data-cart-reward-fill]');
        this.messageEl = this.querySelector('[data-cart-reward-message]');
        this.tierEls = [...this.querySelectorAll('[data-cart-reward-tier]')];
        this.tiers = this.tierEls.map((tierEl) => ({
            id: tierEl.dataset.tierId,
            threshold: Number(tierEl.dataset.threshold || 0),
            label: tierEl.dataset.label || '',
            progressPosition: Number(tierEl.dataset.progressPosition || 0),
            el: tierEl,
        }));
        this.maxThreshold = Number(this.dataset.maxThreshold || 0);
        this.subtotal = Number(this.dataset.subtotal || 0);
        this.awayTemplate = this.dataset.awayTemplate || '';
        this.completeTemplate = this.dataset.completeTemplate || '';
    }

    connectedCallback() {
        this.setSubtotal(this.subtotal);
    }

    formatMoney(cents) {
        if (window.Shopify && Shopify.formatMoney) {
            return Shopify.formatMoney(cents, Shopify.money_format);
        }

        return `$${(cents / 100).toFixed(2)}`;
    }

    escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value;
        return div.innerHTML;
    }

    setSubtotal(subtotal) {
        if (!this.maxThreshold || !this.fillEl || !this.messageEl) return;

        const progress = this.getProgressForSubtotal(subtotal);
        this.fillEl.style.width = `${progress}%`;
        this.fillEl.setAttribute('aria-valuenow', Math.round(progress));

        this.tiers.forEach((tier) => {
            const reached = subtotal >= tier.threshold;
            const wasReached = tier.el.classList.contains('is-complete');
            tier.el.classList.toggle('is-complete', reached);

            if (reached && !wasReached) {
                tier.el.classList.add('cp-pop');
                setTimeout(() => tier.el.classList.remove('cp-pop'), 420);
            }
        });

        const nextTier = this.tiers.reduce((next, tier) => {
            if (subtotal >= tier.threshold) return next;
            if (!next || tier.threshold < next.threshold) return tier;
            return next;
        }, null);

        if (!nextTier) {
            this.messageEl.innerHTML = this.completeTemplate;
            return;
        }

        const remaining = Math.max(0, nextTier.threshold - subtotal);
        this.messageEl.innerHTML = this.awayTemplate
            .replace('[amount]', this.formatMoney(remaining))
            .replace('[reward]', this.escapeHtml(nextTier.label));
    }

    getProgressForSubtotal(subtotal) {
        const stops = this.tiers
            .filter((tier) => tier.threshold > 0)
            .map((tier) => ({
                threshold: tier.threshold,
                position:
                    tier.progressPosition ||
                    Math.min(100, (tier.threshold / this.maxThreshold) * 100),
            }))
            .sort((a, b) => a.threshold - b.threshold);

        let previousStop = { threshold: 0, position: 0 };

        for (const stop of stops) {
            if (subtotal <= stop.threshold) {
                const thresholdRange = stop.threshold - previousStop.threshold;
                const subtotalInRange = Math.max(
                    0,
                    subtotal - previousStop.threshold
                );
                const rangeProgress = thresholdRange
                    ? subtotalInRange / thresholdRange
                    : 1;

                return Math.min(
                    100,
                    previousStop.position +
                        (stop.position - previousStop.position) * rangeProgress
                );
            }

            previousStop = stop;
        }

        return 100;
    }
}

customElements.get('cart-reward-progress') ||
    customElements.define('cart-reward-progress', CartRewardProgress);

class DrawerCart extends HTMLElement {
    constructor() {
        super();
        this.cartIdentifier = this.getAttribute('render-section');
        this.sectionId =
            this.getAttribute('data-section-id') || this.cartIdentifier;
        this.checkoutContainer = document.querySelector(
            '[cart-checkout-container]'
        );
        this.operationQueue = Promise.resolve();
        this.pendingOperationCount = 0;
        this.initialRewardSyncStarted = false;
        this.suppressedRewardGiftTiers = new Map();

        this.handleLineItem = this.handleLineItem.bind(this);
        this.handleCartRefreshEvent = this.handleCartRefreshEvent.bind(this);

        this.addEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.handleLineItem
        );
        document.addEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.handleCartRefreshEvent
        );
        document.addEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.handleCartRefreshEvent
        );
    }

    connectedCallback() {
        const initialItemCount = Number(
            this.getAttribute('data-cart-item-count') ||
                this.getAttribute('data-cartItemCount') ||
                0
        );

        if (
            !['cart-drawer', 'main-cart'].includes(this.cartIdentifier) ||
            initialItemCount === 0 ||
            this.initialRewardSyncStarted
        ) {
            return;
        }

        this.initialRewardSyncStarted = true;
        this.fetchAndRenderCartItems({ detail: { openDrawer: false } });
    }

    getSectionRenderUtil() {
        return HELPER_UTIL.sectionRenderUtil(this.cartIdentifier);
    }

    _getCartItemCount({ sections }) {
        const cartSection =
            sections[this.sectionId] || sections[this.cartIdentifier];
        if (!cartSection) return 0;

        const parsedSection = new DOMParser().parseFromString(
            cartSection,
            'text/html'
        );

        return Number(
            parsedSection
                .querySelector(this.tagName)
                ?.getAttribute('data-cart-item-count') ||
                parsedSection
                    .querySelector(this.tagName)
                    ?.getAttribute('data-cartItemCount') ||
                0
        );
    }

    renderCart({ sections, openDrawer = true }) {
        this.renderCartContents({ sections });
        this.renderElementsBasedOnCartItems({ sections });
        initCartDrawerTimer();

        const drawer = this.closest('cart-drawer-component');
        if (
            openDrawer &&
            this.cartIdentifier === 'cart-drawer' &&
            drawer &&
            !drawer.isDrawerOpen
        ) {
            HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, this, {
                drawerType: 'cart-drawer',
            });
        }
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerToastMessage,
            document,
            {
                toastType: 'common-errors',
                messageType,
                message:
                    responseObj.message ||
                    responseObj.description ||
                    'Something went wrong. Please try again.',
            }
        );
    }

    setCartBusy(isBusy) {
        this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
        this.querySelectorAll('quantity-selector').forEach((selector) => {
            selector.setAttribute('is-disabled', isBusy ? 'true' : 'false');
        });
    }

    enqueueOperation(operation) {
        this.pendingOperationCount += 1;
        this.setCartBusy(true);

        const task = this.operationQueue.then(operation);
        this.operationQueue = task.catch(() => undefined);

        return task.finally(() => {
            this.pendingOperationCount -= 1;
            if (this.pendingOperationCount === 0) {
                this.setCartBusy(false);
            }
        });
    }

    async requestCartJson(path, options = {}) {
        const response = await fetch(`${window.Shopify.routes.root}${path}`, {
            ...options,
            headers: {
                Accept: 'application/json',
                ...(options.body ? { 'Content-Type': 'application/json' } : {}),
                ...options.headers,
            },
        });
        let responseObj = {};

        try {
            responseObj = await response.json();
        } catch (error) {
            responseObj = {};
        }

        if (!response.ok) {
            const requestError = new Error(
                responseObj.description ||
                    responseObj.message ||
                    `Cart request failed with status ${response.status}`
            );
            requestError.status = response.status;
            requestError.responseObj = responseObj;
            throw requestError;
        }

        return responseObj;
    }

    getRewardGiftTiers() {
        try {
            const globalRewardGiftConfig = document.querySelector(
                '[data-cart-reward-gift-config]'
            );
            const serializedTiers =
                this.dataset.rewardGiftTiers ||
                globalRewardGiftConfig?.textContent ||
                '[]';

            const configuredTiers = JSON.parse(serializedTiers)
                .map((tier) => ({
                    id: String(tier.id || ''),
                    threshold: Number(tier.threshold || 0),
                    giftVariantId: Number(tier.giftVariantId || 0),
                }))
                .filter(
                    (tier) =>
                        tier.id && tier.threshold > 0 && tier.giftVariantId > 0
                );

            return [
                ...new Map(
                    configuredTiers.map((tier) => [tier.id, tier])
                ).values(),
            ];
        } catch (error) {
            console.warn('Cart reward configuration is invalid', error);
            return [];
        }
    }

    isOwnedRewardGift(item) {
        return (
            String(item.properties?.[CART_REWARD_GIFT_PROPERTY] || '') ===
            'true'
        );
    }

    getRewardGiftTierId(item) {
        return String(item.properties?.[CART_REWARD_GIFT_TIER_PROPERTY] || '');
    }

    isFreeRewardGiftLine(item) {
        return (
            Number(item.final_line_price || 0) ===
            CART_REWARD_GIFT_FREE_LINE_PRICE
        );
    }

    isRewardGiftTierSuppressed(tierId, subtotal) {
        return this.suppressedRewardGiftTiers.get(tierId) === subtotal;
    }

    suppressUndiscountedRewardGiftTier(tierId, subtotal) {
        if (!tierId) return;

        this.suppressedRewardGiftTiers.set(tierId, subtotal);
        console.warn(
            `Reward gift tier "${tierId}" was removed because Shopify did not discount it to $0.`
        );
    }

    clearInactiveRewardGiftSuppressions(giftTiers, subtotal) {
        const tierMap = new Map(giftTiers.map((tier) => [tier.id, tier]));

        this.suppressedRewardGiftTiers.forEach((failedSubtotal, tierId) => {
            const tier = tierMap.get(tierId);
            if (
                !tier ||
                subtotal < tier.threshold ||
                failedSubtotal !== subtotal
            ) {
                this.suppressedRewardGiftTiers.delete(tierId);
            }
        });
    }

    getEligibleSubtotal(cart) {
        return cart.items.reduce((subtotal, item) => {
            return this.isOwnedRewardGift(item)
                ? subtotal
                : subtotal + Number(item.final_line_price || 0);
        }, 0);
    }

    async reconcileRewardGifts() {
        const giftTiers = this.getRewardGiftTiers();
        const cart = await this.requestCartJson('cart.js');
        const subtotal = this.getEligibleSubtotal(cart);
        this.clearInactiveRewardGiftSuppressions(giftTiers, subtotal);

        const tierMap = new Map(giftTiers.map((tier) => [tier.id, tier]));
        const keptTierIds = new Set();
        const giftLineUpdates = {};

        cart.items
            .filter((item) => this.isOwnedRewardGift(item))
            .sort((firstItem, secondItem) => {
                return (
                    Number(this.isFreeRewardGiftLine(secondItem)) -
                    Number(this.isFreeRewardGiftLine(firstItem))
                );
            })
            .forEach((item) => {
                const tierId = this.getRewardGiftTierId(item);
                const tier = tierMap.get(tierId);
                const canKeepLine =
                    tier &&
                    subtotal >= tier.threshold &&
                    Number(item.variant_id) === tier.giftVariantId &&
                    !keptTierIds.has(tierId);

                if (!canKeepLine) {
                    giftLineUpdates[item.key] = 0;
                    return;
                }

                keptTierIds.add(tierId);
                if (Number(item.quantity) !== 1) {
                    giftLineUpdates[item.key] = 1;
                    return;
                }

                if (!this.isFreeRewardGiftLine(item)) {
                    giftLineUpdates[item.key] = 0;
                    this.suppressUndiscountedRewardGiftTier(tierId, subtotal);
                }
            });

        const giftsToAdd = giftTiers
            .filter((tier) => {
                return (
                    subtotal >= tier.threshold &&
                    !keptTierIds.has(tier.id) &&
                    !this.isRewardGiftTierSuppressed(tier.id, subtotal)
                );
            })
            .map((tier) => ({
                id: tier.giftVariantId,
                quantity: 1,
                properties: {
                    [CART_REWARD_GIFT_PROPERTY]: 'true',
                    [CART_REWARD_GIFT_TIER_PROPERTY]: tier.id,
                },
            }));

        if (Object.keys(giftLineUpdates).length) {
            await this.requestCartJson('cart/update.js', {
                method: 'POST',
                body: JSON.stringify({ updates: giftLineUpdates }),
            });
        }

        if (giftsToAdd.length) {
            await this.requestCartJson('cart/add.js', {
                method: 'POST',
                body: JSON.stringify({ items: giftsToAdd }),
            });

            const verifiedCart = await this.requestCartJson('cart.js');
            const undiscountedGiftLineUpdates = {};

            verifiedCart.items
                .filter((item) => this.isOwnedRewardGift(item))
                .forEach((item) => {
                    const tierId = this.getRewardGiftTierId(item);
                    const tier = tierMap.get(tierId);
                    const isExpectedGiftLine =
                        tier &&
                        subtotal >= tier.threshold &&
                        Number(item.variant_id) === tier.giftVariantId;

                    if (isExpectedGiftLine && !this.isFreeRewardGiftLine(item)) {
                        undiscountedGiftLineUpdates[item.key] = 0;
                        this.suppressUndiscountedRewardGiftTier(
                            tierId,
                            subtotal
                        );
                    }
                });

            if (Object.keys(undiscountedGiftLineUpdates).length) {
                await this.requestCartJson('cart/update.js', {
                    method: 'POST',
                    body: JSON.stringify({
                        updates: undiscountedGiftLineUpdates,
                    }),
                });
            }
        }
    }

    cartCheckoutEl() {
        const hideContainerClass = 'hide';
        return {
            show: () =>
                this.checkoutContainer?.classList.remove(hideContainerClass),
            hide: () => this.checkoutContainer?.classList.add(hideContainerClass),
        };
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        this.setAttribute('data-cart-item-count', String(cartItemCount));

        if (cartItemCount > 0) {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.showCartRecommendations,
                this
            );
            initCartDrawerTimer();
            return;
        }

        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.hideCartRecommendations,
            this
        );
        resetCartDrawerTimer();
        if (window.cartDrawerTimerIntervalId) {
            clearInterval(window.cartDrawerTimerIntervalId);
            window.cartDrawerTimerIntervalId = null;
        }
    }

    handleCartRefreshEvent(event) {
        this.fetchAndRenderCartItems(event);
    }

    handleLineItem({ detail }) {
        const lineItemKey = detail.key;
        const quantity = Number(detail.quantity);

        this.enqueueOperation(async () => {
            await this.requestCartJson('cart/change.js', {
                method: 'POST',
                body: JSON.stringify({ id: lineItemKey, quantity }),
            });
            await this.reconcileRewardGifts();
            const sections = await this.fetchCartSections();
            this.renderCart({ sections, openDrawer: false });
        }).catch((error) => {
            if (error.status === 422) {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerlineItemError,
                    this,
                    {
                        errorObj: {
                            lineItem: lineItemKey,
                            responseObj: error.responseObj || {
                                message: error.message,
                            },
                            messageType: 'error',
                        },
                    }
                );
                return;
            }

            console.warn('Cart line update failed', error);
            this.showDrawerToastMessage({
                responseObj: error.responseObj || { message: error.message },
                messageType: 'error',
            });
        });
    }

    getHTMLFromString(dataToParse, elementToGet) {
        return new DOMParser()
            .parseFromString(dataToParse, 'text/html')
            .querySelector(elementToGet);
    }

    renderCartContents({ sections }) {
        this.getSectionRenderUtil()
            .getSectionsObject()
            .forEach((sectionDescriptor) => {
                const sectionHTML = sections[sectionDescriptor.sectionId];
                if (!sectionHTML) return;

                const nextSection = this.getHTMLFromString(
                    sectionHTML,
                    sectionDescriptor.queryElement
                );
                if (!nextSection) return;

                document
                    .querySelectorAll(sectionDescriptor.queryElement)
                    .forEach((section) => {
                        section.innerHTML = nextSection.innerHTML;
                    });
            });
    }

    async fetchCartSections() {
        const sectionsToRender = this.getSectionRenderUtil().getSectionsString();
        const sectionsUrl = encodeURIComponent(window.location.pathname);
        const data = await this.requestCartJson(
            `cart/update.js?sections=${encodeURIComponent(
                sectionsToRender
            )}&sections_url=${sectionsUrl}`
        );

        if (!data.sections) {
            throw new Error('Cart sections were missing from the response');
        }

        return data.sections;
    }

    fetchAndRenderCartItems(event) {
        const shouldOpenDrawer = event?.detail?.openDrawer !== false;

        return this.enqueueOperation(async () => {
            await this.reconcileRewardGifts();
            const sections = await this.fetchCartSections();
            this.renderCart({ sections, openDrawer: shouldOpenDrawer });
        }).catch((error) => {
            console.warn('Cart refresh failed', error);
            this.showDrawerToastMessage({
                responseObj: error.responseObj || { message: error.message },
                messageType: 'error',
            });
        });
    }

    disconnectedCallback() {
        this.removeEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.handleLineItem
        );
        document.removeEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.handleCartRefreshEvent
        );
        document.removeEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.handleCartRefreshEvent
        );
    }
}

customElements.get('drawer-cart') ||
    customElements.define('drawer-cart', DrawerCart);

class CartPage extends DrawerCart {
    constructor() {
        super();
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        if (cartItemCount > 0) {
            // this.updateCartTotal(data);
            if (this.checkoutContainer) {
                this.cartCheckoutEl().show();
            }
        } else {
            if (this.checkoutContainer) {
                this.cartCheckoutEl().hide();
            }
        }
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        const toastEl = this.querySelector('toast-message');
        toastEl?.toast({
            message:
                responseObj.message ||
                responseObj.description ||
                'Something went wrong. Please try again.',
            messageType: messageType,
        });
    }
}

customElements.get('cart-page') || customElements.define('cart-page', CartPage);

class DrawerItem extends HTMLElement {
    constructor() {
        super();
        this.itemKey = this.getAttribute('item-key');
        this.isRewardGift = this.hasAttribute('data-reward-gift');
        this.removeItemElement = this.querySelectorAll('[remove-lineItem]');
        this.handleRemoveClick = this.dispatchLineItemUpdate.bind(this);
        this.handleRemoveKeyup = this.handleRemoveKeyup.bind(this);
        this.handleQuantityChange = this.dispatchLineItemUpdate.bind(this);
        this.handleLineItemError = this.showLineItemError.bind(this);

        [...this.removeItemElement].forEach((el) => {
            el.addEventListener('click', this.handleRemoveClick);
            el.addEventListener('keyup', this.handleRemoveKeyup);
        });
        this.addEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.handleQuantityChange
        );
        document.addEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.handleLineItemError
        );
    }

    handleRemoveKeyup(event) {
        if (event.key === 'Enter') this.dispatchLineItemUpdate();
    }

    dispatchLineItemUpdate(e) {
        if (this.closest('drawer-cart, cart-page')?.getAttribute('aria-busy') === 'true') {
            return;
        }

        if (this.isRewardGift) {
            this.resetQuantitySelector();
            return;
        }

        const productQuantity = e?.detail.quantity;
        const detail = {
            key: this.itemKey,
            quantity: productQuantity ? productQuantity : '0',
        };
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.OnLineItemUpdate,
            this,
            detail
        );
    }

    handleLineItemErrorUpdate(errorObj) {
        const customMessageComponent =
            this.querySelector('custom-message') ||
            this.nextElementSibling?.querySelector('custom-message');
        customMessageComponent?.showCustomMessage({
            errorMessage:
                errorObj.responseObj.message || errorObj.responseObj.description,
            messageType: errorObj.messageType,
        });
    }

    resetQuantitySelector() {
        const quantityInput = this.querySelector('quantity-selector input');
        if (quantityInput) {
            quantityInput.value = this.getAttribute('item-quantity');
        }
    }

    showLineItemError(event) {
        const { errorObj } = event.detail;
        if (errorObj && errorObj.lineItem === this.itemKey) {
            this.handleLineItemErrorUpdate(errorObj);
            this.resetQuantitySelector();
        }
    }

    disconnectedCallback() {
        this.removeEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.handleQuantityChange
        );
        [...this.removeItemElement].forEach((el) => {
            el.removeEventListener('click', this.handleRemoveClick);
            el.removeEventListener('keyup', this.handleRemoveKeyup);
        });
        document.removeEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.handleLineItemError
        );
    }
}

customElements.get('drawer-item') ||
    customElements.define('drawer-item', DrawerItem);



//More checkout button animation 

class AdditionalCheckoutButtonsAnimation extends HTMLElement {
    constructor() {
        super();
        this.toggleButton = this.querySelector("[toggle-payment-buttons]")
        this.toggleButton?.addEventListener("click", this.togglePaymentButtons.bind(this))
        this.buttonsWrapper = this.querySelector("[checkout-buttons-wrapper]")

    }

    togglePaymentButtons(e) {
        e.preventDefault()
        this.toggleButton.classList.toggle("show-payment-options")
        this.setAttribute("style", `--button-wrapper-height:${this.buttonsWrapper?.firstElementChild.offsetHeight}px`)
        this.buttonsWrapper?.classList.toggle("active")
    }

}

customElements.get('additional-checkout-buttons-animation') ||
    customElements.define("additional-checkout-buttons-animation", AdditionalCheckoutButtonsAnimation)

document.addEventListener('DOMContentLoaded', initCartDrawerTimer);
